const abandonmentCleanupRegistry = new Map();

export function registerAbandonmentDetectorCleanup(key, cleanup) {
    if (!key) return;
    if (typeof cleanup !== "function") return;
    abandonmentCleanupRegistry.set(key, cleanup);
}

export function stopAbandonmentDetector(key) {
    if (!key) return;
    const cleanup = abandonmentCleanupRegistry.get(key);
    if (typeof cleanup === "function") {
        cleanup();
    }
    abandonmentCleanupRegistry.delete(key);
}

export function createAbandonmentDetector(onAbandon, timeoutMs = 30 * 60 * 1000) {
    if (typeof window !== "undefined" && __IS_CSR__) {
        try {
            const testFlag = localStorage.getItem("TEST_ABANDONMENT_30S");
            if (testFlag === "true") {
                timeoutMs = 30 * 1000;
            }
        } catch (e) {
            // Silently ignore if localStorage access fails
        }
    }

    let hasCompleted = false;
    let hasAbandoned = false;
    let lastActivityAt = Date.now();
    let inactivityIntervalId = null;
    let handleVisibilityChange = null;
    let handleBeforeUnload = null;
    let handlePageHide = null;
    let handlePopState = null;
    let isRefreshing = false;
    let currentNavigationIndex = window.navigation?.currentEntry?.index ?? null;

    const safeCall = (payload) => {
        if (hasAbandoned) return;
        hasAbandoned = true;
        if (typeof onAbandon === "function") {
            onAbandon(payload);
        }
    };

    const handleExit = () => {
        if (hasCompleted) return;
        safeCall({ didExitPage: true, isTimedOut: false });
    };

    const updateActivity = () => {
        lastActivityAt = Date.now();
    };


    const start = () => {
        if (typeof window === "undefined") {
            return () => { };
        }
        handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                handleExit();
            }
        };
        handleBeforeUnload = () => {
            isRefreshing = true;
            handleExit();
        };
        handlePageHide = (event) => {
            if (event?.persisted) return;
            if (isRefreshing) return;
            handleExit();
        };
        handlePopState = () => {
            const nextNavigationIndex = window.navigation?.currentEntry?.index ?? null;

            if (
                typeof currentNavigationIndex === "number"
                && typeof nextNavigationIndex === "number"
                && nextNavigationIndex < currentNavigationIndex
            ) {
                handleExit();
            }

            currentNavigationIndex = nextNavigationIndex;
        };
        const currentState = window.history.state;

        if (!currentState || currentState.__abandonmentDetectorInitialized !== true) {
            window.history.replaceState(
                {
                    ...(currentState || {}),
                    __abandonmentDetectorInitialized: true,
                },
                "",
                window.location.href
            );
        }
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("beforeunload", handleBeforeUnload);
        window.addEventListener("pagehide", handlePageHide);
        window.addEventListener("popstate", handlePopState);

        ["click", "keydown", "scroll", "mousemove", "touchstart"].forEach((evt) => {
            window.addEventListener(evt, updateActivity, { passive: true });
        });

        inactivityIntervalId = window.setInterval(() => {
            if (hasCompleted) return;
            if (Date.now() - lastActivityAt >= timeoutMs) {
                safeCall({ didExitPage: false, isTimedOut: true });
            }
        }, 1000);

        return () => {
            hasCompleted = true;
            hasAbandoned = true;
            if (handleVisibilityChange) {
                document.removeEventListener("visibilitychange", handleVisibilityChange);
            }
            if (handleBeforeUnload) {
                window.removeEventListener("beforeunload", handleBeforeUnload);
            }
            if (handlePageHide) {
                window.removeEventListener("pagehide", handlePageHide);
            }
            if (handlePopState) {
                window.removeEventListener("popstate", handlePopState);
            }
            ["click", "keydown", "scroll", "mousemove", "touchstart"].forEach((evt) => {
                window.removeEventListener(evt, updateActivity);
            });
            if (inactivityIntervalId) {
                window.clearInterval(inactivityIntervalId);
                inactivityIntervalId = null;
            }
        };
    };

    const complete = () => {
        hasCompleted = true;
    };

    return {
        start,
        complete,
        getState: () => ({ hasCompleted, hasAbandoned, lastActivityAt }),
    };
}